import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { MessagesListView } from './MessagesListView';
import { getMessageDetails, getMessageFolder, getMessageFolderSelection } from 'Src/helpers/service/admin/messages';
import {
  getCreatedAtWithTime,
  getMessageFolderStatus,
  getMessageTakePart,
  getMessageTypeByKind
} from 'Src/helpers/accessor/accessor';
import { MessageDefinition } from 'Src/types/message';
import { getSelectOptionsForMessageKindByType } from 'Src/helpers/table/select';
import { MESSAGE_KIND, MESSAGE_TABS } from 'Src/consts/message';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { MessageAdditionalSummary } from 'Src/components/MessageSummary/MessageAdditionalSummary';
import { MessageArchiveSummary } from 'Src/components/MessageSummary/MessageArchiveSummary';
import { MessageInboxOutboxSummary } from 'Src/components/MessageSummary/MessageInboxOutboxSummary';
import { getSchoolEvent } from '../../../../helpers/service/admin/event';
import { SchoolEvent } from '../../../../models/event';
import { getEventGeneratedName } from '../../../../helpers/accessor/accessor';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Message type',
    field: 'messageType',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getMessageTypeByKind
  },
  {
    text: 'Student name',
    field: 'studentFirstName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Student surname',
    field: 'studentLastName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'lastName']
  },
  {
    text: 'Created at',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAtWithTime
  },
  {
    text: 'Status',
    field: 'messageStatus',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getMessageFolderStatus
  },
  {
    text: 'Take part',
    field: 'messageTakePart',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getMessageTakePart
  }
];

const hasRequiredArchiveFilters = (filters: any): boolean => {
  const kinds = filters.messageType;
  const createdAt = filters.createdAt || {};
  return Array.isArray(kinds) && kinds.length > 0 && Boolean(createdAt.from) && Boolean(createdAt.to);
};

interface State {
  items: MessageDefinition[];
  currentPage: number;
  selectedItems: MessageDefinition[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  userTags: any[];
  total: number;
  basePath: string;
  schoolEvent: SchoolEvent;
  currentTabIndex: number;
  isDetailsLoading: boolean;
  requiresFilters: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  mode?: 'archive' | 'outbox';
}

export class MessagesArchive extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      schoolEvent: undefined,
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      userTags: [],
      total: 0,
      basePath: '',
      currentTabIndex: 0,
      isDetailsLoading: false,
      requiresFilters: props.mode !== 'outbox'
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search || nextProps.mode !== this.props.mode) {
      this.setState({
        isLoading: true,
        selectedItems: [],
        isSelectAllChecked: false,
        currentTabIndex: 0
      });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const { history, user } = props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const isOutbox = props.mode === 'outbox';
    const requiresFilters = !isOutbox && !hasRequiredArchiveFilters(filters);
    if (requiresFilters) {
      this.setState({
        currentPage: page,
        sortDirection,
        sortColumnsName,
        isShowFilter: false,
        isDataFiltered,
        filters,
        items: [],
        total: 0,
        basePath: history.location.pathname,
        isLoading: false,
        requiresFilters: true
      });
      return BPromise.resolve(true);
    }

    return getMessageFolder(user, isOutbox ? 'outbox' : 'archive', serverQueryFilter).then(result => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: result.items,
        total: result.count,
        basePath: history.location.pathname,
        isLoading: false,
        requiresFilters: Boolean(result.requiresFilters)
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setSelectedItems(selectedItemsUpdated);
  };

  onOpenItem = (index: number): void => {
    const selectedItem = this.state.items[index];
    if (selectedItem) this.setSelectedItems([selectedItem]);
  };

  setSelectedItems = (selectedItemsUpdated: MessageDefinition[]): void => {
    const { user } = this.props;
    const message = selectedItemsUpdated[0];
    const eventId = message ? propz.get(message, ['eventId']) : undefined;

    if (selectedItemsUpdated.length === 1) {
      this.setState({
        schoolEvent: undefined,
        selectedItems: selectedItemsUpdated,
        currentTabIndex: 0,
        isDetailsLoading: true
      });

      const eventPromise =
        typeof eventId !== 'undefined'
          ? getSchoolEvent(user, eventId).catch(() => undefined)
          : BPromise.resolve(undefined);

      BPromise.all([getMessageDetails(user, message.id), eventPromise])
        .then(([messageDetails, schoolEvent]) => {
          const selectedMessage = this.state.selectedItems[0];
          if (selectedMessage && selectedMessage.id === message.id) {
            this.setState({ selectedItems: [messageDetails], schoolEvent, isDetailsLoading: false });
          }
        })
        .catch(() => {
          const selectedMessage = this.state.selectedItems[0];
          if (selectedMessage && selectedMessage.id === message.id) this.setState({ isDetailsLoading: false });
        });
    } else {
      this.setState({
        schoolEvent: undefined,
        selectedItems: selectedItemsUpdated,
        currentTabIndex: 0,
        isDetailsLoading: false
      });
    }
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const where = getServerFieldSectionWhere(this.state.filters);
    const folder = this.props.mode === 'outbox' ? 'outbox' : 'archive';
    this.setState({ isLoading: true });
    getMessageFolderSelection(this.props.user, folder, where, this.state.total)
      .then(selectedItems =>
        this.setState({
          selectedItems,
          isSelectAllChecked: true,
          isDetailsLoading: false,
          isLoading: false
        })
      )
      .catch(() => this.setState({ isLoading: false }));
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isDetailsLoading: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.chipValues) {
      nextFilters = { ...nextFilters, [filterField]: options.chipValues };
    } else if (options && options.modernRange) {
      nextFilters = { ...nextFilters, [filterField]: options.modernRange };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters,
      requiresFilters: this.props.mode !== 'outbox' && !hasRequiredArchiveFilters(nextFilters)
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    if (this.props.mode !== 'outbox' && !hasRequiredArchiveFilters(filters)) {
      const incompleteSearch = isFilterExist2(filters) ? getSearchFilter(filters) : '';
      this.setState({
        requiresFilters: true,
        items: [],
        total: 0,
        selectedItems: [],
        isSelectAllChecked: false,
        isShowFilter: false
      });
      this.props.history.push({
        pathname: this.state.basePath,
        search: incompleteSearch
      });
      return;
    }
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      isDataFiltered: false,
      filters: getFilters2(COLUMNS, {}),
      requiresFilters: this.props.mode !== 'outbox'
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onCancelFilterClick = (): void => {
    const filters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    this.setState({
      isShowFilter: false,
      filters,
      requiresFilters: this.props.mode !== 'outbox' && !hasRequiredArchiveFilters(filters)
    });
  };

  onRemoveFilterClick = (field: string): void => {
    const filters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    delete filters[field];
    this.setState({ filters }, this.onApplyFilterClick);
  };

  renderMessageTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      requiresFilters
    } = this.state;

    const actionItems = [];

    const isOutbox = this.props.mode === 'outbox';
    const allowedKinds = isOutbox
      ? [MESSAGE_KIND.ClubParticipantInviteMessage, MESSAGE_KIND.EventInvitationMessage]
      : [
          MESSAGE_KIND.AbsenceMessage,
          MESSAGE_KIND.EventParticipationMessage,
          MESSAGE_KIND.EventInvitationMessage,
          MESSAGE_KIND.ClubParticipantInviteMessage,
          MESSAGE_KIND.TournamentConfirmationRequestMessage
        ];
    const invitationStatuses = [
      { text: 'Pending', value: 'PENDING' },
      { text: 'Accepted', value: 'ACCEPTED' },
      { text: 'Declined', value: 'REJECTED' },
      { text: 'Prebooked', value: 'PREBOOKED' },
      { text: 'Outdated', value: 'OUTDATED' },
      { text: 'Not sent', value: 'NOT_SENT' },
      { text: 'Cancelled', value: 'CANCELED' },
      { text: 'Got it', value: 'GOT_IT' },
      { text: 'Waiting list', value: 'WAITING_LIST' },
      { text: 'Waiting list not booked', value: 'WAITING_LIST_NOT_BOOKED' },
      { text: 'Waiting list can book', value: 'WAITING_LIST_CAN_BOOK' },
      { text: 'Authorising', value: 'AUTHORISING' },
      { text: 'Refunded', value: 'REFUNDED' }
    ];
    const filterOptions = {
      messageType: getSelectOptionsForMessageKindByType().filter(option => allowedKinds.includes(String(option.value))),
      messageStatus: isOutbox
        ? invitationStatuses
        : [
            ...invitationStatuses,
            { text: 'Authorised', value: 'AUTHORISED' },
            { text: 'Unauthorised', value: 'UNAUTHORISED' },
            { text: 'Available', value: 'AVAILABLE' },
            { text: 'Unavailable', value: 'UNAVAILABLE' }
          ]
    };

    return (
      <MessagesListView
        title={this.props.mode === 'outbox' ? 'Outbox' : 'Archive'}
        user={this.props.user}
        viewKey={this.props.mode === 'outbox' ? 'messages.outbox' : 'messages.archive'}
        items={items}
        columns={COLUMNS}
        selectedItems={selectedItems}
        currentPage={currentPage}
        total={total}
        filters={filters}
        isShowFilter={isShowFilter}
        isDataFiltered={isDataFiltered}
        filterOptions={filterOptions}
        actions={actionItems}
        onItemClick={this.onItemClick}
        onOpenItem={this.onOpenItem}
        onFilterToggle={this.onTableFilterClick}
        onFilterChange={this.onTableFilterChange}
        onApplyFilter={this.onApplyFilterClick}
        onClearFilter={this.onClearFilterClick}
        onRemoveFilter={this.onRemoveFilterClick}
        onCancelFilter={this.onCancelFilterClick}
        onSelectAllPage={this.onSelectAllOnPageClick}
        onSelectAll={this.onSelectAllClick}
        onClearSelection={this.onUnselectAllClick}
        onPage={this.setCurrentPageParams}
        requiresFilters={requiresFilters}
        filterHint={isOutbox ? undefined : 'Archive requires at least one message type and a complete date range.'}
      />
    );
  }

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems, schoolEvent } = this.state;
    const { user } = this.props;

    const message = selectedItems[0];

    let eventName = '';
    if (typeof schoolEvent !== 'undefined') {
      eventName = getEventGeneratedName(schoolEvent, { user: user });
    }

    switch (currentTabIndex) {
      case 0:
        return this.props.mode === 'outbox' ? (
          <MessageInboxOutboxSummary message={message} user={user} schoolEvent={schoolEvent} eventName={eventName} />
        ) : (
          <MessageArchiveSummary message={message} user={user} schoolEvent={schoolEvent} eventName={eventName} />
        );
      case 1:
        return <MessageAdditionalSummary message={message} />;
    }
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  renderSummary(): React.ReactNode {
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a message above</div>;
    }

    const { currentTabIndex } = this.state;
    const summaryTabs = this.getSummaryTabs();

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={MESSAGE_TABS} currentTabIndex={currentTabIndex}>
        {summaryTabs}
      </GenericSummary>
    );
  }

  render() {
    const { isLoading, isDetailsLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = 'messagesFolder';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        <div className="messagesModern">
          <section className="messagesModern__list">{this.renderMessageTable()}</section>
          {this.isOneItemSelected() && (
            <>
              <div className="messagesModern__scrim" onClick={this.onUnselectAllClick} aria-hidden="true" />
              <aside className="messagesModern__drawer" role="dialog" aria-modal="true" aria-label="Message details">
                <div className="messagesModern__drawerHeader">
                  <span className="messagesModern__drawerIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
                      <path d="m5 7 7 5 7-5" />
                    </svg>
                  </span>
                  <div>
                    <span className="messagesModern__drawerBadge">
                      {this.props.mode === 'outbox' ? 'Outbox' : 'Archive'}
                    </span>
                    <h2>{getMessageTypeByKind(this.state.selectedItems[0])}</h2>
                    <p>
                      {propz.get(this.state.selectedItems[0], ['playerDetailsData', 'firstName'], '')}{' '}
                      {propz.get(this.state.selectedItems[0], ['playerDetailsData', 'lastName'], '')}
                    </p>
                  </div>
                  <button type="button" className="messagesModern__close" onClick={this.onUnselectAllClick}>
                    ×
                  </button>
                </div>
                {isDetailsLoading ? (
                  <div className="messagesModern__detailsLoading">
                    <Loader />
                  </div>
                ) : (
                  this.renderSummary()
                )}
              </aside>
            </>
          )}
        </div>
      </div>
    );
  }
}
